import React, { useState, useEffect, useCallback, useRef } from "react";
import { JsonRpc } from "@proton/js";
import { normalizeTimestamp, formatDate } from "../utils/dateUtils";
import { createResolutionProposalTransaction } from "../utils/crypto";
import Loader from "./loader/Loader";
import { TRADING_TOKEN } from "../configs/config";

interface Market {
  id: number;
  question: string;
  category: string;
  expire: number;
  expireSec: number;
  resolved: boolean;
  outcome: number;
  image_url?: string;
  outcomes_count: number;
  version?: number;
  q_yes?: number;
  q_no?: number;
  b?: number;
  total_collateral_in?: number;
  collected_fees?: number;
}

interface Outcome {
  outcome_id: number;
  name: string;
  display_order: number;
}

interface MarketMeta {
  description: string;
  resolution_criteria: string;
}

interface UserPosition {
  sharesYes: number;
  sharesNo: number;
}

interface ResProposal {
  proposal_id: number;
  market_id: number;
  proposer: string;
  created_at: number;
  voting_end_at: number;
  is_resolved: boolean;
  winning_outcome_id: number;
  decided_by_admin: boolean;
  paused: boolean;
}

interface PropVote {
  account: string;
  chosen_outcome: number;
  voted_at: number;
}

interface ResConfig {
  next_proposal_id: number;
  resolution_expiry_sec: number;
  eligible_voters_count: number;
  winning_threshold: number;
}

interface ResolutionComment {
  id: number;
  market_id: number;
  proposal_id: number | null;
  user_account: string;
  comment_text: string;
  image_url: string | null;
  created_at: number;
  updated_at: number | null;
  parent_comment_id: number | null;
  is_deleted: number;
  deleted_by: string | null;
  deleted_at: number | null;
}

interface ResReactionData {
  emoji: string;
  count: number;
  users: string[];
}

interface MarketResolutionDetailProps {
  session: any;
  marketId: number;
  tab: "open" | "voting" | "stalled" | "closed" | "illiquid";
  onBack: () => void;
}

const VOTING_THRESHOLD = 5;
const ELIGIBLE_VOTERS = 21;

const MarketResolutionDetail: React.FC<MarketResolutionDetailProps> = ({
  session,
  marketId,
  tab,
  onBack,
}) => {
  const [market, setMarket] = useState<Market | null>(null);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [marketMeta, setMarketMeta] = useState<MarketMeta | null>(null);
  const [userPosition, setUserPosition] = useState<UserPosition | null>(null);
  const [loading, setLoading] = useState(true);
  const [votingCountdown, setVotingCountdown] = useState<string>("");
  const [voteLoading, setVoteLoading] = useState(false);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [proposalSuccess, setProposalSuccess] = useState(false);
  const [proposal, setProposal] = useState<ResProposal | null>(null);
  const [voteCounts, setVoteCounts] = useState<Record<number, number>>({});
  const [userVote, setUserVote] = useState<PropVote | null>(null);
  const [isEligibleVoter, setIsEligibleVoter] = useState(false);
  const [resConfig, setResConfig] = useState<ResConfig | null>(null);
  const [txStatus, setTxStatus] = useState<
    "idle" | "pending" | "success" | "failed"
  >("idle");
  const [txMessage, setTxMessage] = useState<string>("");

  // Comment/discussion state
  const [resComments, setResComments] = useState<ResolutionComment[]>([]);
  const [newResComment, setNewResComment] = useState("");
  const [resCommentLoading, setResCommentLoading] = useState(false);
  const [resReplyingTo, setResReplyingTo] = useState<ResolutionComment | null>(
    null,
  );
  const [resCommentAdmins, setResCommentAdmins] = useState<string[]>([]);
  const [resCommentReactions, setResCommentReactions] = useState<
    Record<number, Record<string, ResReactionData>>
  >({});
  const [resUserReactions, setResUserReactions] = useState<
    Record<number, string | null>
  >({});
  const [editingResCommentId, setEditingResCommentId] = useState<number | null>(
    null,
  );
  const [editResCommentText, setEditResCommentText] = useState("");
  const [editResCommentLoading, setEditResCommentLoading] = useState(false);
  const [commentImageFile, setCommentImageFile] = useState<File | null>(null);
  const [commentImagePreview, setCommentImagePreview] = useState("");
  const [commentImageUploading, setCommentImageUploading] = useState(false);
  const [xpredHolders, setXpredHolders] = useState<string[]>([]);
  const commentTextareaRef = useRef<HTMLTextAreaElement>(null);

  const fetchMarketData = useCallback(async () => {
    try {
      const rpc = new JsonRpc(
        process.env.REACT_APP_PROTON_ENDPOINT || "https://proton.eosusa.io",
      );
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";

      const marketResult = await rpc.get_table_rows({
        code: contractName,
        scope: contractName,
        table: "markets3",
        lower_bound: marketId,
        upper_bound: marketId,
        limit: 1,
      });

      if (marketResult.rows.length > 0) {
        const marketData = marketResult.rows[0];
        setMarket({
          ...marketData,
          expireSec: normalizeTimestamp(marketData.expire),
          outcomes_count: marketData.outcomes_count || 2,
          resolved: !!marketData.resolved,
          version: marketData.version || 0,
          q_yes: marketData.q_yes || 0,
          q_no: marketData.q_no || 0,
          b: marketData.b || 500_000_000,
          total_collateral_in: marketData.total_collateral_in || 0,
          collected_fees: marketData.collected_fees || 0,
        });

        const outcomesResult = await rpc.get_table_rows({
          code: contractName,
          scope: marketId.toString(),
          table: "outcomes",
          limit: 100,
        });

        const fetchedOutcomes: Outcome[] = outcomesResult.rows.map(
          (row: any) => ({
            outcome_id: row.outcome_id,
            name: row.name,
            display_order: row.display_order,
          }),
        );

        fetchedOutcomes.sort((a, b) => a.display_order - b.display_order);
        setOutcomes(fetchedOutcomes);

        if (session && marketData.version >= 2) {
          try {
            const userAccount = session.auth.actor.toString();
            const userPosResult = await rpc.get_table_rows({
              code: contractName,
              scope: marketId.toString(),
              table: "poslmsr",
              lower_bound: userAccount,
              upper_bound: userAccount,
              limit: 1,
            });

            if (userPosResult.rows.length > 0) {
              const pos = userPosResult.rows[0];
              setUserPosition({
                sharesYes: pos.shares_yes / 1_000_000,
                sharesNo: pos.shares_no / 1_000_000,
              });
            } else {
              setUserPosition(null);
            }
          } catch (error) {
            console.error("Error fetching user position:", error);
            setUserPosition(null);
          }
        }

        try {
          const configResult = await rpc.get_table_rows({
            code: contractName,
            scope: contractName,
            table: "resconfig",
            limit: 1,
          });

          if (configResult.rows.length > 0) {
            const configData = configResult.rows[0];
            setResConfig({
              next_proposal_id: configData.next_proposal_id || 1,
              resolution_expiry_sec: configData.resolution_expiry_sec || 172800,
              eligible_voters_count: configData.eligible_voters_count || 21,
              winning_threshold: configData.winning_threshold || 5,
            });
          }

          const proposalsResult = await rpc.get_table_rows({
            code: contractName,
            scope: contractName,
            table: "proposals",
            limit: 200,
          });

          const marketProposal = proposalsResult.rows.find(
            (row: any) => row.market_id === marketId && !row.is_resolved,
          );

          if (marketProposal) {
            const fetchedProposal: ResProposal = {
              proposal_id: marketProposal.proposal_id,
              market_id: marketProposal.market_id,
              proposer: marketProposal.proposer,
              created_at: normalizeTimestamp(marketProposal.created_at),
              voting_end_at: normalizeTimestamp(marketProposal.voting_end_at),
              is_resolved: !!marketProposal.is_resolved,
              winning_outcome_id:
                marketProposal.winning_outcome_id != null
                  ? marketProposal.winning_outcome_id
                  : 255,
              decided_by_admin: !!marketProposal.decided_by_admin,
              paused: !!marketProposal.paused,
            };
            setProposal(fetchedProposal);

            const allVotesResult = await rpc.get_table_rows({
              code: contractName,
              scope: fetchedProposal.proposal_id.toString(),
              table: "propvotes",
              limit: 200,
            });
            const counts: Record<number, number> = {};
            for (const v of allVotesResult.rows) {
              counts[v.chosen_outcome] = (counts[v.chosen_outcome] || 0) + 1;
            }
            setVoteCounts(counts);

            if (session) {
              const userAccount = session.auth.actor.toString();

              const voterSnapsResult = await rpc.get_table_rows({
                code: contractName,
                scope: contractName,
                table: "votersnaps",
                lower_bound: userAccount,
                upper_bound: userAccount,
                limit: 1,
              });

              if (
                voterSnapsResult.rows.length > 0 &&
                voterSnapsResult.rows[0].account === userAccount
              ) {
                const voterSnapData = voterSnapsResult.rows[0];
                const userProposals = voterSnapData.proposals || [];
                const eligibility = userProposals.find(
                  (p: any) => p.proposal_id === fetchedProposal.proposal_id,
                );

                if (eligibility) {
                  setIsEligibleVoter(true);

                  const votesResult = await rpc.get_table_rows({
                    code: contractName,
                    scope: fetchedProposal.proposal_id.toString(),
                    table: "propvotes",
                    lower_bound: userAccount,
                    upper_bound: userAccount,
                    limit: 1,
                  });

                  if (votesResult.rows.length > 0) {
                    const voteData = votesResult.rows[0];
                    setUserVote({
                      account: voteData.account,
                      chosen_outcome: voteData.chosen_outcome,
                      voted_at: normalizeTimestamp(voteData.voted_at),
                    });
                  } else {
                    setUserVote(null);
                  }
                } else {
                  setIsEligibleVoter(false);
                  setUserVote(null);
                }
              } else {
                setIsEligibleVoter(false);
                setUserVote(null);
              }
            } else {
              setIsEligibleVoter(false);
              setUserVote(null);
            }
          } else {
            setProposal(null);
            setVoteCounts({});
            setIsEligibleVoter(false);
            setUserVote(null);
          }
        } catch (error) {
          console.error("Error fetching proposal data:", error);
          setProposal(null);
          setVoteCounts({});
          setIsEligibleVoter(false);
          setUserVote(null);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching market data:", error);
      setLoading(false);
    }
  }, [marketId, session]);

  const fetchMarketMeta = useCallback(async () => {
    try {
      const response = await fetch(
        `/api/market_meta.php?market_id=${marketId}`,
      );
      const data = await response.json();
      if (data.success && data.data) {
        setMarketMeta({
          description: data.data.description || "",
          resolution_criteria: data.data.resolution_criteria || "",
        });
      }
    } catch (error) {
      console.error("Error fetching market metadata:", error);
    }
  }, [marketId]);

  useEffect(() => {
    fetchMarketData();
    fetchMarketMeta();
    const interval = setInterval(fetchMarketData, 5000);
    return () => clearInterval(interval);
  }, [fetchMarketData, fetchMarketMeta]);

  const getVotingEndMs = (): number => {
    if (proposal) {
      return proposal.voting_end_at * 1000;
    }
    if (market) {
      const configVotingSec = resConfig
        ? resConfig.resolution_expiry_sec
        : 172800;
      return (market.expireSec + configVotingSec) * 1000;
    }
    return 0;
  };

  const getTotalVotes = (): number => {
    return Object.values(voteCounts).reduce((sum, count) => sum + count, 0);
  };

  const getVotingThreshold = (): number => {
    return resConfig ? resConfig.winning_threshold : VOTING_THRESHOLD;
  };

  const getEligibleVoters = (): number => {
    return resConfig ? resConfig.eligible_voters_count : ELIGIBLE_VOTERS;
  };

  const hasReachedThreshold = (): boolean => {
    return getTotalVotes() >= getVotingThreshold();
  };

  useEffect(() => {
    if (!proposal) return;

    const updateCountdowns = () => {
      const now = Date.now();
      const votingEndMs = getVotingEndMs();

      const votingRemainingMs = votingEndMs - now;
      if (votingRemainingMs > 0) {
        const hours = Math.floor(votingRemainingMs / (1000 * 60 * 60));
        const minutes = Math.floor(
          (votingRemainingMs % (1000 * 60 * 60)) / (1000 * 60),
        );
        const seconds = Math.floor((votingRemainingMs % (1000 * 60)) / 1000);
        setVotingCountdown(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setVotingCountdown("Ended");
      }
    };

    updateCountdowns();
    const interval = setInterval(updateCountdowns, 1000);
    return () => clearInterval(interval);
  }, [proposal, resConfig]);

  const getActivePhase = (): "voting" | "ended" => {
    const now = Date.now();
    const votingEndMs = getVotingEndMs();
    if (now < votingEndMs) return "voting";
    return "ended";
  };

  const getMarketStatus = () => {
    if (!market) return "unknown";
    if (market.resolved) return "resolved";

    if (proposal) {
      if (proposal.is_resolved) return "resolved";
      if (proposal.paused) return "unresolved";
      const now = Math.floor(Date.now() / 1000);
      if (now < proposal.voting_end_at) return "voting";
      return "unresolved";
    }

    const now = Math.floor(Date.now() / 1000);
    if (market.expireSec > now) return "active";
    return "unresolved";
  };

  const fetchTopXpredHolders = async (
    count: number,
  ): Promise<{ voters: string[]; balances: number[] }> => {
    const url =
      "https://www.api.bloks.io/proton/tokens?type=topHolders&chain=proton&contract=tokencreate&symbol=XPRED&limit=500";

    const response = await fetch(url, {
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Bloks API HTTP error: ${response.status}`);
    }

    const data = await response.json();

    if (!data || !Array.isArray(data)) {
      throw new Error("Invalid response from Bloks API");
    }

    const holders: { account: string; balance: number }[] = [];

    for (const row of data) {
      if (!Array.isArray(row) || row.length < 2) continue;

      const account = row[0];
      const balanceStr = row[1];

      if (!account || typeof account !== "string") continue;

      const numericBalance = parseFloat(balanceStr);
      const intBalance = Math.round(numericBalance * 1_000_000);

      if (intBalance <= 0) continue;

      holders.push({ account, balance: intBalance });
    }

    holders.sort((a, b) => b.balance - a.balance);

    const exemptedRaw = process.env.REACT_APP_EXEMPTED_ACCOUNTS || "";
    const exemptedSet = new Set(
      exemptedRaw
        .split(",")
        .map((a) => a.trim().toLowerCase())
        .filter((a) => a.length > 0),
    );
    const eligible = holders.filter(
      (h) => !exemptedSet.has(h.account.toLowerCase()),
    );
    const topHolders = eligible.slice(0, count);

    return {
      voters: topHolders.map((h) => h.account),
      balances: topHolders.map((h) => h.balance),
    };
  };

  const handleCreateProposal = async () => {
    if (!session) {
      alert("Please connect your wallet to submit a proposal");
      return;
    }

    setProposalLoading(true);
    setTxStatus("pending");
    setTxMessage("Fetching eligible voters...");
    try {
      const eligibleCount = resConfig
        ? resConfig.eligible_voters_count
        : ELIGIBLE_VOTERS;

      const { voters, balances } = await fetchTopXpredHolders(eligibleCount);

      if (voters.length === 0) {
        setTxStatus("failed");
        setTxMessage("Could not find any XPRED token holders.");
        setProposalLoading(false);
        return;
      }

      setTxMessage("Submitting resolution proposal...");

      await createResolutionProposalTransaction(session, {
        market_id: marketId,
        voters,
        balances,
      });

      setTxStatus("success");
      setTxMessage(
        "Proposal submitted successfully! It is now in the voting phase.",
      );
      setProposalSuccess(true);
      fetchMarketData();
    } catch (error) {
      console.error("Error creating proposal:", error);
      setTxStatus("failed");
      setTxMessage("Failed to submit proposal: " + error);
    } finally {
      setProposalLoading(false);
    }
  };

  const handleVote = async (outcomeId: number) => {
    if (!session || !proposal) {
      alert("Please connect your wallet to vote");
      return;
    }

    if (!isEligibleVoter) {
      alert("You are not eligible to vote on this proposal");
      return;
    }

    setVoteLoading(true);
    setTxStatus("pending");
    setTxMessage("Submitting vote...");
    try {
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";

      await session.transact({
        actions: [
          {
            account: contractName,
            name: "vote",
            authorization: [
              {
                actor: session.auth.actor,
                permission: session.auth.permission,
              },
            ],
            data: {
              voter: session.auth.actor.toString(),
              proposal_id: proposal.proposal_id,
              chosen_outcome: outcomeId,
            },
          },
        ],
      });

      const outcomeName =
        outcomes.find((o) => o.outcome_id === outcomeId)?.name ||
        "Outcome " + outcomeId;
      setTxStatus("success");
      setTxMessage(`Vote submitted successfully! You voted: ${outcomeName}`);
      fetchMarketData();
    } catch (error) {
      console.error("Error voting:", error);
      setTxStatus("failed");
      setTxMessage("Failed to submit vote: " + error);
    } finally {
      setVoteLoading(false);
    }
  };

  const formatVolume = () => {
    if (!market) return "0 " + TRADING_TOKEN;
    const volume = Math.floor((market.total_collateral_in || 0) / 1_000_000);
    return `${volume} ${TRADING_TOKEN}`;
  };

  // ===== Resolution Comment Functions =====

  const canComment = tab === "voting" || tab === "stalled";
  const showComments =
    tab === "voting" || tab === "stalled" || tab === "closed";

  const fetchResComments = useCallback(async () => {
    try {
      const response = await fetch(
        `/api/resolution_comments.php?market_id=${marketId}`,
      );
      const data = await response.json();
      if (data.success) {
        setResComments(data.comments || []);
        if (data.admins) setResCommentAdmins(data.admins);
      }
    } catch (error) {
      console.error("Error fetching resolution comments:", error);
    }
  }, [marketId]);

  const fetchResReactions = useCallback(async () => {
    try {
      let url = `/api/resolution_reactions.php?market_id=${marketId}`;
      if (session) {
        url += `&user_account=${session.auth.actor.toString()}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setResCommentReactions(data.reactions_by_comment || {});
        setResUserReactions(data.user_reactions || {});
      }
    } catch (error) {
      console.error("Error fetching resolution reactions:", error);
    }
  }, [marketId, session]);

  const fetchXpredHolders = useCallback(async () => {
    try {
      const url =
        "https://www.api.bloks.io/proton/tokens?type=topHolders&chain=proton&contract=tokencreate&symbol=XPRED&limit=500";
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      const data = await response.json();
      if (!data || !Array.isArray(data)) return;

      const exemptedRaw = process.env.REACT_APP_EXEMPTED_ACCOUNTS || "";
      const exemptedSet = new Set(
        exemptedRaw
          .split(",")
          .map((a: string) => a.trim().toLowerCase())
          .filter((a: string) => a.length > 0),
      );

      const holders: string[] = [];
      for (const row of data) {
        if (!Array.isArray(row) || row.length < 2) continue;
        const account = row[0];
        if (!account || typeof account !== "string") continue;
        const numericBalance = parseFloat(row[1]);
        if (Math.round(numericBalance * 1_000_000) <= 0) continue;
        if (!exemptedSet.has(account.toLowerCase())) {
          holders.push(account);
        }
      }
      setXpredHolders(holders);
    } catch (error) {
      console.error("Error fetching XPRED holders:", error);
    }
  }, []);

  useEffect(() => {
    if (showComments) {
      fetchResComments();
      fetchResReactions();
      fetchXpredHolders();
    }
  }, [showComments, fetchResComments, fetchResReactions, fetchXpredHolders]);

  const handleResCommentImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      alert("Only JPG, PNG, and WebP images are allowed");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert("Image must be less than 2MB");
      return;
    }
    setCommentImageFile(file);
    setCommentImagePreview(URL.createObjectURL(file));
  };

  const removeCommentImage = () => {
    setCommentImageFile(null);
    setCommentImagePreview("");
  };

  const handlePostResComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session) return;

    if (!newResComment.trim() && !commentImageFile) {
      alert("Please enter a comment or attach an image");
      return;
    }

    if (newResComment.length > 1000) {
      alert("Comment is too long (max 1000 characters)");
      return;
    }

    setResCommentLoading(true);
    try {
      let imageUrl: string | null = null;

      if (commentImageFile) {
        setCommentImageUploading(true);
        const formData = new FormData();
        formData.append("file", commentImageFile);
        const uploadRes = await fetch("/api/upload.php", {
          method: "POST",
          body: formData,
        });
        const uploadData = await uploadRes.json();
        if (uploadData.success) {
          imageUrl = uploadData.url;
        } else {
          alert("Failed to upload image");
          setResCommentLoading(false);
          setCommentImageUploading(false);
          return;
        }
        setCommentImageUploading(false);
      }

      const response = await fetch("/api/resolution_comments.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          market_id: marketId,
          proposal_id: proposal?.proposal_id || null,
          user_account: session.auth.actor.toString(),
          comment_text: newResComment.trim(),
          image_url: imageUrl,
          parent_comment_id: resReplyingTo ? resReplyingTo.id : null,
        }),
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        alert("Server error");
        return;
      }

      if (data.success) {
        setNewResComment("");
        setResReplyingTo(null);
        setCommentImageFile(null);
        setCommentImagePreview("");
        fetchResComments();
      } else {
        alert("Failed to post comment: " + (data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error posting resolution comment:", error);
      alert("Failed to post comment");
    } finally {
      setResCommentLoading(false);
    }
  };

  const handleResReply = (comment: ResolutionComment) => {
    if (!session) {
      alert("Please connect your wallet to reply");
      return;
    }
    setResReplyingTo(comment);
    setNewResComment("");
    setTimeout(() => {
      if (commentTextareaRef.current) commentTextareaRef.current.focus();
    }, 100);
  };

  const handleEditResComment = async (commentId: number) => {
    if (!session) return;
    const trimmed = editResCommentText.trim();
    if (!trimmed) {
      alert("Comment text is required");
      return;
    }
    if (trimmed.length > 1000) {
      alert("Comment is too long (max 1000 characters)");
      return;
    }

    setEditResCommentLoading(true);
    try {
      const response = await fetch("/api/resolution_comments.php", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          comment_id: commentId,
          user_account: session.auth.actor.toString(),
          comment_text: trimmed,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setEditingResCommentId(null);
        setEditResCommentText("");
        fetchResComments();
      } else {
        alert("Failed to edit comment: " + (data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error editing resolution comment:", error);
    } finally {
      setEditResCommentLoading(false);
    }
  };

  const handleDeleteResComment = async (commentId: number) => {
    if (!session) return;
    const userAccount = session.auth.actor.toString();
    if (!resCommentAdmins.includes(userAccount)) {
      alert("You do not have permission to delete comments");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this comment?"))
      return;

    try {
      const response = await fetch("/api/resolution_comments.php", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          comment_id: commentId,
          user_account: userAccount,
        }),
      });
      const data = await response.json();
      if (data.success) {
        fetchResComments();
      } else {
        alert("Failed to delete comment");
      }
    } catch (error) {
      console.error("Error deleting resolution comment:", error);
    }
  };

  const handleResReaction = async (commentId: number, reactionType: string) => {
    if (!session) {
      alert("Please connect your wallet to react");
      return;
    }
    try {
      const response = await fetch("/api/resolution_reactions.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          comment_id: commentId,
          market_id: marketId,
          user_account: session.auth.actor.toString(),
          reaction_type: reactionType,
        }),
      });
      const data = await response.json();
      if (data.success) {
        fetchResReactions();
      }
    } catch (error) {
      console.error("Error updating reaction:", error);
    }
  };

  const isXpredHolder = (account: string): boolean => {
    return xpredHolders.includes(account);
  };

  const renderResCommentText = (comment: ResolutionComment) => {
    if (comment.is_deleted) {
      return (
        <div className="res-comment-text deleted-text">[Moderator Deleted]</div>
      );
    }
    return (
      <div className="res-comment-text">
        {comment.comment_text && <p>{comment.comment_text}</p>}
        {comment.image_url && (
          <div className="res-comment-image">
            <img
              src={comment.image_url}
              alt="Comment attachment"
              onClick={() => window.open(comment.image_url!, "_blank")}
            />
          </div>
        )}
      </div>
    );
  };

  const renderResReactions = (commentId: number) => {
    const reactions = resCommentReactions[commentId];
    const userReaction = resUserReactions[commentId];
    const defaultReactions: Record<string, ResReactionData> = {
      like: { emoji: "\uD83D\uDC4D", count: 0, users: [] },
      dislike: { emoji: "\uD83D\uDC4E", count: 0, users: [] },
    };
    const displayReactions = reactions || defaultReactions;

    return (
      <div className="res-reactions">
        {(["like", "dislike"] as const).map((type) => {
          const reaction = displayReactions[type] || defaultReactions[type];
          const isActive = userReaction === type;
          return (
            <button
              key={type}
              className={`res-reaction-btn ${isActive ? "active" : ""} ${reaction.count > 0 ? "has-reactions" : ""}`}
              onClick={() => handleResReaction(commentId, type)}
              disabled={!session || tab === "closed"}
              title={`${type === "like" ? "Like" : "Dislike"}${reaction.count > 0 ? ` (${reaction.count})` : ""}`}
            >
              <span className="res-reaction-emoji">{reaction.emoji}</span>
              {reaction.count > 0 && (
                <span className="res-reaction-count">{reaction.count}</span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  const currentUser = session?.auth?.actor?.toString() || "";

  if (loading) {
    return <Loader label="Loading market details..." />;
  }

  if (!market) {
    return (
      <div className="market-not-found">
        <h2>Market Not Found</h2>
        <p>The requested market could not be found.</p>
        <button onClick={onBack} className="back-button">
          Back to Markets
        </button>
      </div>
    );
  }

  const status = getMarketStatus();
  const activePhase = getActivePhase();

  return (
    <div className="market-resolution-detail">
      <button onClick={onBack} className="back-button">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Back to Resolution
      </button>

      <div className="resolution-detail-header">
        {market.image_url && (
          <div className="resolution-detail-image">
            <img
              src={market.image_url}
              alt={market.question}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
        )}
        <div className="resolution-detail-info">
          <span className="resolution-detail-category">{market.category}</span>
          <h1 className="resolution-detail-question">{market.question}</h1>
          <div className="resolution-detail-meta">
            <span
              className={`status-badge ${tab === "stalled" ? "stalled" : tab === "closed" ? "resolved" : status}`}
            >
              {tab === "stalled" && "Stalled"}
              {tab === "closed" && "Resolved"}
              {tab !== "stalled" &&
                tab !== "closed" &&
                status === "resolved" &&
                "Resolved"}
              {tab !== "stalled" &&
                tab !== "closed" &&
                status === "voting" &&
                "Awaiting Resolution"}
              {tab !== "stalled" &&
                tab !== "closed" &&
                status === "active" &&
                "Active"}
              {tab !== "stalled" &&
                tab !== "closed" &&
                status === "unresolved" &&
                "Unresolved"}
            </span>
            <span className="market-id">Market #{market.id}</span>
            {tab === "voting" && isEligibleVoter && (
              <span className="eligible-voter-badge">Eligible to Vote</span>
            )}
          </div>
        </div>
      </div>

      {txStatus !== "idle" && (
        <div className={`tx-status-banner ${txStatus}`}>
          {txStatus === "pending" && (
            <svg
              className="spinner"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
            </svg>
          )}
          {txStatus === "success" && (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <path d="M22 4L12 14.01l-3-3" />
            </svg>
          )}
          {txStatus === "failed" && (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M15 9l-6 6M9 9l6 6" />
            </svg>
          )}
          <span>{txMessage}</span>
          {txStatus !== "pending" && (
            <button
              className="tx-dismiss"
              onClick={() => {
                setTxStatus("idle");
                setTxMessage("");
              }}
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      {tab === "voting" && (status === "voting" || status === "unresolved") && (
        <div className="resolution-capsule-banner">
          <div
            className={`capsule-section capsule-voting ${activePhase === "voting" ? "active" : "completed"}`}
          >
            <div className="capsule-section-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="10" />
              </svg>
            </div>
            <div className="capsule-section-content">
              <span className="capsule-section-label">Voting Period</span>
              <span className="capsule-section-timer">
                {activePhase === "voting" ? votingCountdown : "Ended"}
              </span>
            </div>
          </div>
        </div>
      )}

      {status === "unresolved" && tab === "voting" && (
        <div className="unresolved-banner">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M15 9l-6 6M9 9l6 6" />
          </svg>
          <div className="unresolved-banner-content">
            <span className="unresolved-banner-title">Market Unresolved</span>
            <span className="unresolved-banner-text">
              {!hasReachedThreshold()
                ? `This market did not reach the required voting threshold (${getTotalVotes()}/${getVotingThreshold()} of ${getEligibleVoters()} voters).`
                : "The voting and dispute periods have ended without resolution."}
            </span>
          </div>
        </div>
      )}

      <div className="resolution-detail-grid">
        <div className="resolution-detail-main">
          <div className="resolution-section market-details-section">
            <h3>Market Details</h3>
            <div className="market-details-content">
              <div className="market-detail-item">
                <h4>Description</h4>
                <p>
                  {marketMeta?.description ||
                    "No description available for this market."}
                </p>
              </div>
              <div className="market-detail-item">
                <h4>How This Market Will Be Resolved</h4>
                <p>
                  {marketMeta?.resolution_criteria ||
                    "Resolution criteria not specified for this market."}
                </p>
              </div>
            </div>
          </div>

          <div className="resolution-section">
            <h3>Market Statistics</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">Total Volume</span>
                <span className="stat-value">{formatVolume()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Expiry Date</span>
                <span className="stat-value">
                  {formatDate(market.expireSec)}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Outcomes</span>
                <span className="stat-value">{market.outcomes_count}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Market Version</span>
                <span className="stat-value">v{market.version}</span>
              </div>
            </div>
          </div>

          {tab === "illiquid" && (
            <div className="resolution-section">
              <div
                className="resolution-info-banner warning"
                style={{ marginBottom: "16px" }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <path d="M12 9v4M12 17h.01" />
                </svg>
                <span>
                  This market is illiquid.{" "}
                  {(market.total_collateral_in || 0) === 0
                    ? "No trades were placed on this market."
                    : "Only one side of this market received trades (one-sided trading)."}{" "}
                  Resolution proposals cannot be created for illiquid markets.
                  An admin can mark this market as refundable.
                </span>
              </div>
            </div>
          )}

          {tab === "open" && !proposal && !proposalSuccess && (
            <div className="resolution-section proposal-form-section">
              <h3>Submit Resolution Proposal</h3>
              {!session ? (
                <p className="voting-message">
                  Connect your wallet to submit a resolution proposal.
                </p>
              ) : (
                <>
                  <p className="proposal-instructions">
                    Please note the following:
                  </p>
                  <ul className="proposal-notes-list">
                    <li>Create a resolution proposal for this market.</li>
                    <li>
                      Once submitted, the top{" "}
                      {resConfig?.eligible_voters_count || 21} XPRED holders
                      will be able to vote on the outcome.
                    </li>
                    <li>
                      The proposal voting period will remain open for{" "}
                      {resConfig
                        ? Math.floor(resConfig.resolution_expiry_sec / 3600)
                        : 24}{" "}
                      hours after submission.
                    </li>
                    <li>
                      A minimum of {resConfig?.winning_threshold || 5} votes
                      will determine the outcome of the proposal.
                    </li>
                  </ul>
                  <div className="proposal-form">
                    <button
                      className="submit-proposal-btn"
                      onClick={handleCreateProposal}
                      disabled={proposalLoading}
                    >
                      {proposalLoading
                        ? "Submitting..."
                        : "Submit Resolution Proposal"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "open" && proposalSuccess && (
            <div className="resolution-section proposal-success-section">
              <div className="proposal-success-content">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="48"
                  height="48"
                >
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                  <path d="M22 4L12 14.01l-3-3" />
                </svg>
                <h3>Proposal Submitted Successfully</h3>
                <p>
                  Your resolution proposal has been submitted and is now in the
                  voting phase. Eligible voters can now vote on your proposed
                  outcome.
                </p>
                <button className="back-to-list-btn" onClick={onBack}>
                  Back to Open Markets
                </button>
              </div>
            </div>
          )}

          {tab === "open" && proposal && !proposalSuccess && (
            <div className="resolution-section">
              <div className="existing-proposal-notice">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
                <div>
                  <strong>Active Proposal Exists</strong>
                  <p>
                    This market already has an active resolution proposal (#
                    {proposal.proposal_id}) by {proposal.proposer}. You can view
                    it in the Voting tab.
                  </p>
                </div>
              </div>
            </div>
          )}

          {tab === "voting" && proposal && (
            <div className="resolution-section voting-statistics">
              <h3>Voting Statistics</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Total Votes Cast</span>
                  <span className="stat-value">{getTotalVotes()}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Votes Required</span>
                  <span className="stat-value">{getVotingThreshold()}</span>
                </div>
                {outcomes.map((outcome) => (
                  <div className="stat-item" key={outcome.outcome_id}>
                    <span className="stat-label">{outcome.name} Votes</span>
                    <span className="stat-value">
                      {voteCounts[outcome.outcome_id] || 0}
                    </span>
                  </div>
                ))}
              </div>
              {userVote && (
                <div className="user-vote-status">
                  You voted:{" "}
                  <strong>
                    {outcomes.find(
                      (o) => o.outcome_id === userVote.chosen_outcome,
                    )?.name || "Outcome " + userVote.chosen_outcome}
                  </strong>
                </div>
              )}
            </div>
          )}

          {tab === "voting" && status === "voting" && proposal && (
            <div className="resolution-section voting-options-section">
              <h3>Cast Your Vote</h3>
              {!session ? (
                <p className="voting-message">
                  Connect your wallet to participate in voting.
                </p>
              ) : !isEligibleVoter ? (
                <p className="voting-message">
                  You are not in the eligible voters list for this proposal.
                  Only the top {getEligibleVoters()} XPRED holders can vote.
                </p>
              ) : userVote ? (
                <p className="voting-message">
                  You have already voted on this proposal. Your vote:{" "}
                  <strong>
                    {outcomes.find(
                      (o) => o.outcome_id === userVote.chosen_outcome,
                    )?.name || "Outcome " + userVote.chosen_outcome}
                  </strong>
                </p>
              ) : (
                <>
                  <p className="voting-message">
                    Select an outcome to cast your vote:
                  </p>
                  <div className="voting-options">
                    {outcomes.map((outcome) => (
                      <button
                        key={outcome.outcome_id}
                        className="vote-option-btn"
                        onClick={() => handleVote(outcome.outcome_id)}
                        disabled={voteLoading}
                      >
                        {voteLoading ? "Voting..." : `Vote ${outcome.name}`}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "closed" && proposal && (
            <div className="resolution-section resolved-section">
              <div className="resolved-banner">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="32"
                  height="32"
                >
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                  <path d="M22 4L12 14.01l-3-3" />
                </svg>
                <div className="resolved-banner-content">
                  <span className="resolved-banner-title">
                    Proposal Resolved
                  </span>
                  <span className="resolved-banner-text">
                    {proposal.decided_by_admin
                      ? "This proposal was resolved by an admin decision."
                      : "This proposal was resolved by reaching the required voting threshold."}
                  </span>
                </div>
              </div>

              {proposal.winning_outcome_id !== 255 && (
                <div className="resolved-winning-outcome">
                  <h3>Winning Outcome</h3>
                  <div className="winning-outcome-display">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      width="24"
                      height="24"
                    >
                      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                      <path d="M22 4L12 14.01l-3-3" />
                    </svg>
                    <span>
                      {outcomes.find(
                        (o) => o.outcome_id === proposal.winning_outcome_id,
                      )?.name || "Unknown"}
                    </span>
                  </div>
                </div>
              )}

              <h3>Voting Summary</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Total Votes Cast</span>
                  <span className="stat-value">{getTotalVotes()}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Votes Required</span>
                  <span className="stat-value">{getVotingThreshold()}</span>
                </div>
                {outcomes.map((outcome) => (
                  <div className="stat-item" key={outcome.outcome_id}>
                    <span className="stat-label">{outcome.name} Votes</span>
                    <span className="stat-value">
                      {voteCounts[outcome.outcome_id] || 0}
                    </span>
                  </div>
                ))}
              </div>
              {proposal.decided_by_admin && (
                <div className="admin-resolved-notice">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    width="18"
                    height="18"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  <span>This proposal was resolved by admin decision.</span>
                </div>
              )}
            </div>
          )}

          {tab === "stalled" && proposal && (
            <div className="resolution-section stalled-section">
              <div className="stalled-banner">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="32"
                  height="32"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <path d="M12 9v4M12 17h.01" />
                </svg>
                <div className="stalled-banner-content">
                  <span className="stalled-banner-title">Proposal Stalled</span>
                  <span className="stalled-banner-text">
                  The voting period for this proposal has ended. Admin action is now required to approve this proposal.
                  </span>
                </div>
              </div>

              <h3>Voting Summary</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Total Votes Cast</span>
                  <span className="stat-value">{getTotalVotes()}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Votes Required</span>
                  <span className="stat-value">{getVotingThreshold()}</span>
                </div>
                {outcomes.map((outcome) => (
                  <div className="stat-item" key={outcome.outcome_id}>
                    <span className="stat-label">{outcome.name} Votes</span>
                    <span className="stat-value">
                      {voteCounts[outcome.outcome_id] || 0}
                    </span>
                  </div>
                ))}
              </div>
              <div className="stalled-admin-notice">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="18"
                  height="18"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                <span>Only an admin can resolve this proposal.</span>
              </div>
            </div>
          )}

          {/* Resolution Discussion Section */}
          {showComments && (
            <div className="resolution-section res-discussion-section">
              <h3>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="20"
                  height="20"
                >
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
                Discussion ({resComments.filter((c) => !c.is_deleted).length})
              </h3>

              {tab === "closed" && (
                <div className="res-comments-closed-notice">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    width="16"
                    height="16"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                  <span>
                    This resolution has been finalized. Comments are closed.
                  </span>
                </div>
              )}

              {/* Comment form - only visible during voting/stalled */}
              {canComment && (
                <form
                  onSubmit={handlePostResComment}
                  className="res-comment-form"
                >
                  {resReplyingTo && (
                    <div className="res-replying-banner">
                      <span>
                        Replying to{" "}
                        <strong>{resReplyingTo.user_account}</strong>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setResReplyingTo(null);
                          setNewResComment("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  <textarea
                    ref={commentTextareaRef}
                    value={newResComment}
                    onChange={(e) => setNewResComment(e.target.value)}
                    placeholder={
                      resReplyingTo
                        ? `Write your reply to ${resReplyingTo.user_account}...`
                        : "Share your thoughts on this resolution..."
                    }
                    maxLength={1000}
                    rows={3}
                    disabled={resCommentLoading || !session}
                  />
                  {commentImagePreview && (
                    <div className="res-comment-image-preview">
                      <img src={commentImagePreview} alt="Preview" />
                      <button
                        type="button"
                        className="res-remove-image-btn"
                        onClick={removeCommentImage}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          width="14"
                          height="14"
                        >
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                  <div className="res-comment-form-footer">
                    <div className="res-comment-form-left">
                      <label
                        className="res-image-upload-btn"
                        title="Attach image"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          width="18"
                          height="18"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="M21 15l-5-5L5 21" />
                        </svg>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleResCommentImageChange}
                          style={{ display: "none" }}
                          disabled={resCommentLoading || !session}
                        />
                      </label>
                      <span className="res-char-count">
                        {newResComment.length}/1000
                      </span>
                    </div>
                    <button
                      type="submit"
                      disabled={
                        resCommentLoading ||
                        (!newResComment.trim() && !commentImageFile) ||
                        !session
                      }
                    >
                      {resCommentLoading
                        ? commentImageUploading
                          ? "Uploading..."
                          : "Posting..."
                        : resReplyingTo
                          ? "Post Reply"
                          : "Post Comment"}
                    </button>
                  </div>
                </form>
              )}

              {/* Comments list */}
              <div className="res-comments-list">
                {resComments.filter((c) => !c.parent_comment_id).length ===
                0 ? (
                  <p className="res-no-comments">
                    No comments yet.{" "}
                    {canComment ? "Be the first to share your thoughts!" : ""}
                  </p>
                ) : (
                  resComments
                    .filter((c) => !c.parent_comment_id)
                    .sort((a, b) => b.created_at - a.created_at)
                    .map((comment) => (
                      <div key={comment.id} className="res-comment-thread">
                        <div
                          className={`res-comment-card ${comment.is_deleted ? "deleted" : ""}`}
                        >
                          <div className="res-comment-header">
                            <span className="res-comment-author">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                width="14"
                                height="14"
                              >
                                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                                <circle cx="12" cy="7" r="4" />
                              </svg>
                              {comment.user_account}
                              {isXpredHolder(comment.user_account) && (
                                <span
                                  className="res-xpred-badge"
                                  title="XPRED Token Holder"
                                >
                                  <svg
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    width="12"
                                    height="12"
                                  >
                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                  </svg>
                                  XPRED Holder
                                </span>
                              )}
                            </span>
                            <span className="res-comment-time">
                              {new Date(
                                comment.created_at * 1000,
                              ).toLocaleString()}
                              {comment.updated_at && (
                                <span className="res-comment-edited">
                                  (edited)
                                </span>
                              )}
                            </span>
                          </div>
                          {editingResCommentId === comment.id ? (
                            <div className="res-edit-comment-form">
                              <textarea
                                value={editResCommentText}
                                onChange={(e) =>
                                  setEditResCommentText(e.target.value)
                                }
                                maxLength={1000}
                                rows={3}
                                disabled={editResCommentLoading}
                              />
                              <div className="res-edit-comment-actions">
                                <span className="res-char-count">
                                  {editResCommentText.length}/1000
                                </span>
                                <button
                                  onClick={() => {
                                    setEditingResCommentId(null);
                                    setEditResCommentText("");
                                  }}
                                  disabled={editResCommentLoading}
                                  className="res-edit-cancel-btn"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() =>
                                    handleEditResComment(comment.id)
                                  }
                                  disabled={
                                    editResCommentLoading ||
                                    !editResCommentText.trim()
                                  }
                                  className="res-edit-save-btn"
                                >
                                  {editResCommentLoading ? "Saving..." : "Save"}
                                </button>
                              </div>
                            </div>
                          ) : (
                            renderResCommentText(comment)
                          )}
                          {!comment.is_deleted &&
                            editingResCommentId !== comment.id && (
                              <div className="res-comment-actions">
                                {canComment && (
                                  <button
                                    onClick={() => handleResReply(comment)}
                                    className="res-reply-btn"
                                  >
                                    <svg
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      width="12"
                                      height="12"
                                    >
                                      <path d="M3 10l7-7v4c11 0 11 9 11 9s-2-5-11-5v4L3 10z" />
                                    </svg>
                                    Reply
                                  </button>
                                )}
                                {session &&
                                  comment.user_account === currentUser &&
                                  canComment && (
                                    <button
                                      onClick={() => {
                                        setEditingResCommentId(comment.id);
                                        setEditResCommentText(
                                          comment.comment_text,
                                        );
                                      }}
                                      className="res-edit-btn"
                                    >
                                      Edit
                                    </button>
                                  )}
                                {session &&
                                  resCommentAdmins.includes(currentUser) && (
                                    <button
                                      onClick={() =>
                                        handleDeleteResComment(comment.id)
                                      }
                                      className="res-delete-btn"
                                    >
                                      Delete
                                    </button>
                                  )}
                              </div>
                            )}
                          {renderResReactions(comment.id)}
                        </div>

                        {/* Replies */}
                        {resComments
                          .filter((r) => r.parent_comment_id === comment.id)
                          .sort((a, b) => a.created_at - b.created_at)
                          .map((reply) => (
                            <div
                              key={reply.id}
                              className={`res-comment-card res-comment-reply ${reply.is_deleted ? "deleted" : ""}`}
                            >
                              <div className="res-comment-header">
                                <span className="res-comment-author">
                                  <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    width="14"
                                    height="14"
                                  >
                                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                  </svg>
                                  {reply.user_account}
                                  {isXpredHolder(reply.user_account) && (
                                    <span
                                      className="res-xpred-badge"
                                      title="XPRED Token Holder"
                                    >
                                      <svg
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        width="12"
                                        height="12"
                                      >
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                      </svg>
                                      XPRED Holder
                                    </span>
                                  )}
                                </span>
                                <span className="res-comment-time">
                                  {new Date(
                                    reply.created_at * 1000,
                                  ).toLocaleString()}
                                  {reply.updated_at && (
                                    <span className="res-comment-edited">
                                      (edited)
                                    </span>
                                  )}
                                </span>
                              </div>
                              {editingResCommentId === reply.id ? (
                                <div className="res-edit-comment-form">
                                  <textarea
                                    value={editResCommentText}
                                    onChange={(e) =>
                                      setEditResCommentText(e.target.value)
                                    }
                                    maxLength={1000}
                                    rows={3}
                                    disabled={editResCommentLoading}
                                  />
                                  <div className="res-edit-comment-actions">
                                    <span className="res-char-count">
                                      {editResCommentText.length}/1000
                                    </span>
                                    <button
                                      onClick={() => {
                                        setEditingResCommentId(null);
                                        setEditResCommentText("");
                                      }}
                                      disabled={editResCommentLoading}
                                      className="res-edit-cancel-btn"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleEditResComment(reply.id)
                                      }
                                      disabled={
                                        editResCommentLoading ||
                                        !editResCommentText.trim()
                                      }
                                      className="res-edit-save-btn"
                                    >
                                      {editResCommentLoading
                                        ? "Saving..."
                                        : "Save"}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                renderResCommentText(reply)
                              )}
                              {!reply.is_deleted &&
                                editingResCommentId !== reply.id && (
                                  <div className="res-comment-actions">
                                    {/* No Reply button on replies - single-level threading */}
                                    {session &&
                                      reply.user_account === currentUser &&
                                      canComment && (
                                        <button
                                          onClick={() => {
                                            setEditingResCommentId(reply.id);
                                            setEditResCommentText(
                                              reply.comment_text,
                                            );
                                          }}
                                          className="res-edit-btn"
                                        >
                                          Edit
                                        </button>
                                      )}
                                    {session &&
                                      resCommentAdmins.includes(
                                        currentUser,
                                      ) && (
                                        <button
                                          onClick={() =>
                                            handleDeleteResComment(reply.id)
                                          }
                                          className="res-delete-btn"
                                        >
                                          Delete
                                        </button>
                                      )}
                                  </div>
                                )}
                              {renderResReactions(reply.id)}
                            </div>
                          ))}
                      </div>
                    ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="resolution-detail-sidebar">
          {userPosition && (
            <div className="sidebar-card">
              <h3>Your Position</h3>
              <div className="position-details">
                {userPosition.sharesYes > 0 && (
                  <div className="position-item">
                    <span className="position-label">
                      {outcomes[0]?.name || "Yes"} Shares:
                    </span>
                    <span className="position-value">
                      {userPosition.sharesYes.toFixed(2)}
                    </span>
                  </div>
                )}
                {userPosition.sharesNo > 0 && (
                  <div className="position-item">
                    <span className="position-label">
                      {outcomes[1]?.name || "No"} Shares:
                    </span>
                    <span className="position-value">
                      {userPosition.sharesNo.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {proposal && (
            <div className="sidebar-card proposal-card">
              <h3>Proposal Info</h3>
              <div className="proposal-info">
                <div className="proposal-detail">
                  <span className="detail-label">Proposer:</span>
                  <span className="detail-value">{proposal.proposer}</span>
                </div>
                <div className="proposal-detail">
                  <span className="detail-label">Eligible Voters:</span>
                  <span className="detail-value">
                    {resConfig?.eligible_voters_count || 21}
                  </span>
                </div>
                <div className="proposal-detail">
                  <span className="detail-label">Status:</span>
                  <span className="detail-value">
                    {proposal.is_resolved
                      ? "Resolved"
                      : proposal.paused
                        ? "Paused"
                        : proposal.decided_by_admin
                          ? "Admin Resolved"
                          : "Voting"}
                  </span>
                </div>
                {proposal.winning_outcome_id !== 255 && (
                  <div className="proposal-detail">
                    <span className="detail-label">Winning Outcome:</span>
                    <span className="detail-value">
                      {outcomes.find(
                        (o) => o.outcome_id === proposal.winning_outcome_id,
                      )?.name || "Unknown"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {status === "resolved" && (
            <div className="sidebar-card resolved-card">
              <h3>Resolution Details</h3>
              <div className="resolved-info">
                <div className="resolved-outcome">
                  <span className="resolved-label">Winning Outcome:</span>
                  <span className="resolved-value">
                    {outcomes.find((o) => o.outcome_id === market.outcome)
                      ?.name || "Unknown"}
                  </span>
                </div>
                <p className="claim-message">
                  If you held winning shares, you can claim your winnings from
                  your Portfolio.
                </p>
              </div>
            </div>
          )}

          {tab === "open" && !proposal && (
            <div className="sidebar-card">
              <h3>Proposal Guidelines</h3>
              <div className="guidelines-content">
                <ul className="guidelines-list">
                  <li>Create a resolution proposal for this market.</li>
                  <li>
                    Once submitted, the top{" "}
                    {resConfig?.eligible_voters_count || 21} XPRED holders will
                    be able to vote on the outcome.
                  </li>
                  <li>
                    The proposal voting period will remain open for{" "}
                    {resConfig
                      ? Math.floor(resConfig.resolution_expiry_sec / 3600)
                      : 24}{" "}
                    hours after submission.
                  </li>
                  <li>
                    A minimum of {resConfig?.winning_threshold || 5} votes will
                    determine the outcome of the proposal.
                  </li>
                </ul>
              </div>
            </div>
          )}

          <div className="sidebar-card">
            <h3>Resolution Timeline</h3>
            <div className="timeline">
              <div
                className={`timeline-item ${status !== "active" ? "completed" : "current"}`}
              >
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <span className="timeline-title">Market Expired</span>
                  <span className="timeline-date">
                    {formatDate(market.expireSec)}
                  </span>
                </div>
              </div>
              <div
                className={`timeline-item ${activePhase !== "voting" && status !== "active" ? "completed" : status === "voting" ? "current" : ""}`}
              >
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <span className="timeline-title">Voting Period</span>
                  <span className="timeline-date">
                    {activePhase === "voting"
                      ? votingCountdown + " remaining"
                      : status !== "active"
                        ? "Completed"
                        : "Pending"}
                  </span>
                </div>
              </div>
              <div
                className={`timeline-item ${status === "resolved" ? "completed" : status === "unresolved" ? "current" : ""}`}
              >
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <span className="timeline-title">Resolution</span>
                  <span className="timeline-date">
                    {status === "resolved"
                      ? "Completed"
                      : status === "unresolved"
                        ? "Unresolved"
                        : "Pending"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketResolutionDetail;
